import { CloseIcon, HelpGlyphIcon } from '@protector/icons';
import { CropIcon } from '@cw-elements/crop-icons';
import { formatArea } from '@protector/utils/l10n';
import type { Crop } from '@protector/utils/types';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import { ReactComponent as CheckGlyphIcon } from '@features/regional-overview/assets/components/check-glyph.svg';
import { COLOR_OPTIONS } from 'config/style';
import STTypo, { STTypoModels } from 'core/shared/typo';
import { isAnyCerealCrop } from 'entities/crop/crop.domain';
import type { StressRiskLevel } from '@features/regional-overview/abiotic-stress/domain/abiotic-stress.models';
import { AbioticStressSection } from '@features/regional-overview/components/abiotic-stress-section/abiotic-stress-section.component';
import { wkSlugToCropIconName } from '@features/regional-overview/utils';
import {
  RiskLevelEnum,
  type RegionalOverviewDiseaseResult,
  type RegionalOverviewError
} from 'querys/regional-overview/regional-overview.query.models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import DiseaseTag from '../disease-tag/disease-tag.component';
import FieldErrorDetailComponent from '../field-error-detail/field-error-detail.component';
import type { FieldInfo } from './field-details-card.models';
import { CloseButton, CropIconWrapper, DetailsLink, Header, RiskTag } from './field-details-card.styles';
import { getRiskLabel } from './field-details-card.utils';
import styles from './field-details-content.module.css';

export interface FieldDetailsContentProps {
  farmName: string;
  fieldName: string;
  area: string;
  riskLevel: RiskLevelEnum;
  crop: Crop;
  fieldInfoItems: FieldInfo[];
  diseases: RegionalOverviewDiseaseResult[];
  error?: RegionalOverviewError;
  stressRiskLevel?: StressRiskLevel;
  showAbioticStress?: boolean;
  inSheet?: boolean;
  onClose: () => void;
  onAccessDetails: () => void;
}

export const FieldDetailsContent = ({
  farmName,
  fieldName,
  area,
  riskLevel,
  crop,
  fieldInfoItems,
  diseases,
  error,
  stressRiskLevel,
  showAbioticStress,
  inSheet = false,
  onClose,
  onAccessDetails
}: FieldDetailsContentProps) => {
  const [t] = useTranslation();
  const filteredDiseases = useMemo(() => diseases.filter(disease => disease.risk !== RiskLevelEnum.NO_RISK), [diseases]);
  const hasDiseases = filteredDiseases.length > 0 && !error;
  const isCereal = isAnyCerealCrop(crop);

  const renderHeaderIcon = () => {
    if (error) {
      return <HelpGlyphIcon fill={COLOR_OPTIONS.GRAY_2} />;
    }

    if (riskLevel === RiskLevelEnum.NO_RISK) {
      return <CheckGlyphIcon color={COLOR_OPTIONS.BLUE_60} />;
    }

    return <CropIcon iconName={wkSlugToCropIconName(crop.wk_slug)} />;
  };

  return (
    <>
      <Header $riskLevel={riskLevel} $isMissingConfiguration={!!error} $sheetTopGap={inSheet}>
        <div className={styles.headerLeft}>
          <CropIconWrapper $riskLevel={riskLevel} $isMissingConfiguration={!!error}>
            {renderHeaderIcon()}
          </CropIconWrapper>
          <div className={styles.headerContent}>
            <STTypo
              type={STTypoModels.TypeOptions.P2}
              fontFamily='Noto Sans'
              fontWeight={400}
              color={COLOR_OPTIONS.NEUTRAL_80}
              lineHeight='20px'
              nowrap
              customClassName={styles.farmName}>
              {farmName}/
            </STTypo>
            <Tooltip title={fieldName}>
              <span className={styles.fieldNameWrapper}>
                <STTypo
                  type={STTypoModels.TypeOptions.H4}
                  fontFamily='Noto Sans'
                  fontWeight={600}
                  color={COLOR_OPTIONS.NEUTRAL_80}
                  lineHeight='28px'
                  customClassName={styles.fieldName}>
                  {fieldName}
                </STTypo>
              </span>
            </Tooltip>
          </div>
        </div>
        <div className={styles.headerRight}>
          {!error && <RiskTag $riskLevel={riskLevel}>{getRiskLabel(riskLevel, t)}</RiskTag>}
          <STTypo
            type={STTypoModels.TypeOptions.P2}
            fontFamily='Noto Sans'
            fontWeight={400}
            color={COLOR_OPTIONS.NEUTRAL_100}
            lineHeight='20px'
            nowrap
            customClassName={styles.areaText}>
            {`${formatArea(area)} ${t('units.measure.HECTARE.abbreviation')}`}
          </STTypo>
        </div>
        <CloseButton type='link' onClick={onClose} icon={<CloseIcon />} aria-label={t('pages.regional_overview.cluster_popup.close')} />
      </Header>

      <main className={classNames(styles.body, { [styles.bodyCentered]: !hasDiseases })}>
        {error ? (
          <FieldErrorDetailComponent error={error} />
        ) : (
          <>
            {fieldInfoItems.map(item => (
              <dl className={styles.fieldInfoContainer} key={item.label}>
                <dt className={styles.fieldLabel}>{item.label}</dt>
                <dd className={styles.fieldValue}>{item.value}</dd>
              </dl>
            ))}

            {hasDiseases && (
              <section className={styles.diseasesSection}>
                <STTypo
                  type={STTypoModels.TypeOptions.P2}
                  fontFamily='Noto Sans'
                  fontWeight={400}
                  color={COLOR_OPTIONS.NEUTRAL_60}
                  lineHeight='20px'
                  customClassName={styles.diseasesTitle}>
                  {t('pages.regional_overview.field_details_card.diseases')}
                </STTypo>
                <ul className={styles.diseasesList}>
                  {filteredDiseases.map(disease => (
                    <li key={disease.disease}>
                      <DiseaseTag riskLevel={disease.risk} diseaseName={disease.disease} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <AbioticStressSection stressRiskLevel={stressRiskLevel} showAbioticStress={showAbioticStress} variant='field' />
          </>
        )}
      </main>

      {isCereal && (
        <footer className={styles.footer}>
          <DetailsLink type='link' onClick={onAccessDetails}>
            {error
              ? t('pages.regional_overview.field_details_card.go_to_field_configuration')
              : t('pages.regional_overview.field_details_card.access_field_view_details')}
          </DetailsLink>
        </footer>
      )}
    </>
  );
};
